import React, { useState } from 'react';
import { Table, Button, Space, Tag, Typography, Input, Popconfirm, message } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import type { ColumnsType } from 'antd/es/table';
import { useUsers, useDeleteUser } from '@/hooks/useUsers';
import type { User } from '@/api/users';
import { extractErrorMessage } from '@/utils/helpers';

const { Title } = Typography;

const UserList: React.FC = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  
  const { data, isLoading } = useUsers({ search, page });
  const deleteUser = useDeleteUser();

  const handleDelete = async (id: string) => {
    try {
      await deleteUser.mutateAsync(id);
      message.success('Usuário excluído com sucesso!');
    } catch (error: any) {
      message.error(extractErrorMessage(error, 'Erro ao excluir usuário'));
    }
  };

  const columns: ColumnsType<User> = [
    {
      title: 'Nome',
      dataIndex: 'full_name',
      key: 'full_name',
    },
    {
      title: 'E-mail',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'Perfil',
      dataIndex: 'profile',
      key: 'profile',
      render: (profile: string) => (
        <Tag color={
          profile === 'SUPER_ADMIN' ? 'red' :
          profile === 'ADMIN' ? 'orange' :
          profile === 'OPERATOR' ? 'blue' : 'green'
        }>
          {profile === 'SUPER_ADMIN' ? 'Super Admin' :
           profile === 'ADMIN' ? 'Admin' :
           profile === 'OPERATOR' ? 'Operador' : 'Visualizador'}
        </Tag>
      ),
    },
    {
      title: 'Empresa',
      dataIndex: 'company_name',
      key: 'company_name',
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (active: boolean) => (
        <Tag color={active ? 'green' : 'red'}>
          {active ? 'Ativo' : 'Inativo'}
        </Tag>
      ),
    },
    {
      title: 'Último Acesso',
      dataIndex: 'last_login',
      key: 'last_login',
      render: (date: string) => date ? new Date(date).toLocaleString('pt-BR') : '-',
    },
    {
      title: 'Ações',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => navigate(`/users/${record.id}/edit`)}
          />
          <Popconfirm
            title="Tem certeza que deseja excluir este usuário?"
            onConfirm={() => handleDelete(record.id)}
            okText="Sim"
            cancelText="Não"
          >
            <Button type="link" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <Title level={4}>Usuários</Title>
        <Space>
          <Input
            placeholder="Buscar usuários..."
            prefix={<SearchOutlined />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: 250 }}
            allowClear
          />
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate('/users/new')}
          >
            Novo Usuário
          </Button>
        </Space>
      </div>

      <Table
        columns={columns}
        dataSource={data?.results || []}
        loading={isLoading}
        rowKey="id"
        pagination={{
          current: page,
          total: data?.count || 0,
          pageSize: 20,
          onChange: setPage,
          showTotal: (total) => `Total: ${total} usuários`,
        }}
      />
    </div>
  );
};

export default UserList;
